import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { LogOut, Menu, X } from "lucide-react";
import { useEffect, useState } from "react";
import { LOGO_URL } from "@/lib/branding";
import { api } from "@/lib/api";
import NotificationBell from "@/components/NotificationBell";
import Avatar from "@/components/Avatar";
import AmbientPlayer from "@/components/AmbientPlayer";

const navItems = [
  { to: "/feed", label: "Feed" },
  { to: "/groups", label: "Prayer Groups" },
  { to: "/cinema", label: "Cinema" },
  { to: "/directory", label: "Directory" },
  { to: "/music", label: "Music" },
  { to: "/inspiration", label: "Inspiration" },
  { to: "/messages", label: "Messages", showBadge: true },
  { to: "/memories", label: "Memories" },
  { to: "/testimonies", label: "Testimonies" },
  { to: "/book-club", label: "Book Club" },
  { to: "/events", label: "Events" },
  { to: "/shop", label: "Shop" },
  { to: "/family", label: "The Family" },
  { to: "/pricing", label: "Membership" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [dmUnread, setDmUnread] = useState(0);

  useEffect(() => {
    if (!user) return;
    let alive = true;
    const fetchCount = async () => {
      try {
        const { data } = await api.get("/messages/unread-count");
        if (alive) setDmUnread(data?.count ?? 0);
      } catch { /* ignore */ }
    };
    fetchCount();
    const id = setInterval(fetchCount, 45_000);
    return () => { alive = false; clearInterval(id); };
  }, [user]);

  return (
    <header className="glass sticky top-0 z-40 border-b border-jj">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 group" data-testid="nav-logo">
          <span className="h-12 w-12 rounded-full bg-jjblue flex items-center justify-center overflow-hidden ring-1 ring-[var(--jj-gold)]/40 shrink-0">
            <img src={LOGO_URL} alt="Jesus Just Us" className="h-11 w-11 object-contain" />
          </span>
          <span className="hidden sm:block font-serif text-2xl text-jjblue tracking-tight leading-none">
            Jesus<span className="text-burgundy"> Just </span>Us
          </span>
        </Link>
        <nav className="hidden md:flex items-center gap-7">
          {user && navItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.to.slice(1)}`}
              className={({ isActive }) =>
                `relative text-sm tracking-wide transition-colors ${isActive ? "text-burgundy" : "text-jjblue/80 hover:text-burgundy"}`
              }
            >
              <span className="inline-flex items-center gap-1.5">
                {n.label}
                {n.showBadge && dmUnread > 0 && (
                  <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-burgundy text-cream text-[10px] font-semibold flex items-center justify-center" data-testid="nav-dm-badge">
                    {dmUnread > 9 ? "9+" : dmUnread}
                  </span>
                )}
              </span>
            </NavLink>
          ))}
        </nav>
        <div className="hidden md:flex items-center gap-3">
          {user ? (
            <>
              <AmbientPlayer />
              <NotificationBell />
              <Link to="/profile" className="text-sm text-jjblue hover:text-burgundy inline-flex items-center gap-2" data-testid="nav-profile">
                <Avatar user={user} size={28} testId="nav-profile-avatar" />
                <span>{user.name} · <span className="text-gold font-medium">{user.tier}</span></span>
              </Link>
              <button onClick={() => { logout(); navigate("/"); }} className="btn-secondary !py-2 !px-4 text-sm flex items-center gap-1.5" data-testid="nav-logout">
                <LogOut className="h-4 w-4" strokeWidth={1.5} /> Sign out
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="text-sm text-jjblue hover:text-burgundy" data-testid="nav-login-link">Sign in</Link>
              <Link to="/register" className="btn-primary !py-2 !px-5 text-sm" data-testid="nav-register-link">Join the Community</Link>
            </>
          )}
        </div>
        <button onClick={() => setOpen(!open)} className="md:hidden text-jjblue" data-testid="nav-mobile-toggle">
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>
      {open && (
        <div className="md:hidden border-t border-jj bg-cream">
          <div className="px-6 py-4 flex flex-col gap-3">
            {user && navItems.map((n) => (
              <NavLink key={n.to} to={n.to} onClick={() => setOpen(false)} data-testid={`mnav-${n.to.slice(1)}`} className="text-jjblue py-1">{n.label}</NavLink>
            ))}
            {user ? (
              <button onClick={() => { setOpen(false); logout(); navigate("/"); }} className="btn-secondary self-start" data-testid="mnav-logout">Sign out</button>
            ) : (
              <div className="flex gap-2">
                <Link to="/login" onClick={() => setOpen(false)} className="btn-secondary text-sm" data-testid="mnav-login">Sign in</Link>
                <Link to="/register" onClick={() => setOpen(false)} className="btn-primary text-sm" data-testid="mnav-register">Join</Link>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
